/**
 * Pure helpers for user-defined table column order (persisted per browser).
 */

export type DropSide = "before" | "after";

/**
 * Reconciles a saved order with the table's current default columns.
 *
 * - Saved ids that no longer exist (renamed column, role without it) are dropped.
 * - Columns missing from the saved order (new column, role that has extra ones)
 *   are inserted right after their closest default predecessor that is present,
 *   so they show up where the default layout would put them instead of at the end.
 * - Duplicates in the saved order are ignored.
 */
export function mergeColumnOrder(
  defaultIds: readonly string[],
  savedIds: readonly string[] | null | undefined,
): string[] {
  const known = new Set(defaultIds);
  const result: string[] = [];
  for (const id of savedIds ?? []) {
    if (known.has(id) && !result.includes(id)) result.push(id);
  }
  defaultIds.forEach((id, idx) => {
    if (result.includes(id)) return;
    let insertAt = 0;
    for (let j = idx - 1; j >= 0; j -= 1) {
      const pos = result.indexOf(defaultIds[j]);
      if (pos !== -1) {
        insertAt = pos + 1;
        break;
      }
    }
    result.splice(insertAt, 0, id);
  });
  return result;
}

/** Moves `fromId` so it sits immediately before/after `toId`. Unknown ids leave the order untouched. */
export function moveColumn(
  order: readonly string[],
  fromId: string,
  toId: string,
  side: DropSide,
): string[] {
  if (fromId === toId || !order.includes(fromId)) return [...order];
  const without = order.filter((id) => id !== fromId);
  const targetIdx = without.indexOf(toId);
  if (targetIdx === -1) return [...order];
  without.splice(side === "after" ? targetIdx + 1 : targetIdx, 0, fromId);
  return without;
}
